import type { KeysOfUnion, LiteralUnion } from "type-fest";

type HasInPredicateType<
  TSubject,
  TKey extends PropertyKey,
  TAllKeys = KeysOfUnion<TSubject>,
  TKnown = Record<TKey, unknown>,
> =
  string extends TAllKeys ?
    // Record<string, ...> case
    TSubject & TKnown
  : TKey extends TAllKeys ?
    // Special handling when we know, we can potentially filter a discriminated
    // union.
    Extract<TSubject, TKnown>
  : TSubject & TKnown;

/**
 * Checks if `key` is a direct or inherited property of an object.
 *
 * This is a proper type-guard so that when it returns true, the property is
 * guaranteed to exist, and therefore TS can properly type check.
 *
 * @param subject the object to query
 * @param key the name of the property to check
 *
 * @see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 */
export const hasIn = <
  TSubject,
  TKey extends LiteralUnion<KeysOfUnion<TSubject>, PropertyKey>,
>(
  subject: TSubject,
  key: TKey,
): subject is HasInPredicateType<TSubject, TKey> =>
  typeof subject === "object" && subject != null && key in subject;

const makeHasInPredicate =
  <TSubject, TKey extends LiteralUnion<KeysOfUnion<TSubject>, PropertyKey>>(
    key: TKey,
  ) =>
  (subject: TSubject): subject is HasInPredicateType<TSubject, TKey> =>
    hasIn(subject, key);

/* eslint-disable @typescript-eslint/ban-ts-comment */
// Below is just to check against types regression. It is never used at runtime.
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
() => {
  const a = { one: "hello", two: true };
  const b = { two: false, three: 12 };

  const c = {} as unknown;
  const d = {} as unknown as typeof a | typeof b;

  const e = {} satisfies Record<string, unknown>;

  if (hasIn(c, "unknownExtra")) {
    c.unknownExtra satisfies unknown;

    //@ts-expect-error
    d.randomExtra satisfies unknown;
  }

  if (hasIn(d, "one")) {
    d.one satisfies string;
    d.two satisfies boolean;
    //@ts-expect-error
    d.three satisfies number;
  }

  if (hasIn(d, "knownButExtra")) {
    d.knownButExtra satisfies unknown;
    d.two satisfies boolean;
    //@ts-expect-error
    d.one satisfies string;
  }

  if (hasIn(e, "extraWithMappedType")) {
    e.extraWithMappedType satisfies unknown;
  }

  [a, b].filter(makeHasInPredicate("one")).map((v) => {
    v.one satisfies string;
    v.two satisfies boolean;
  });
  [a, b].filter(makeHasInPredicate("two")).map((v) => {
    //@ts-expect-error
    v.one satisfies string;
    v.two satisfies boolean;
  });
};
/* eslint-enable */
